import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import HolidayModal from "../../modals/Hrms/HolidayModal";
import LeaveModal from "../../modals/Hrms/LeaveModal";
import LeaveLimitModal from "../../modals/Hrms/LeaveLimitModal";
import FineModal from "../../modals/Hrms/FineModal";
import SessionModal from "../../modals/Hrms/SessionModal";
import TeacherListModal from "../../modals/Hrms/TeacherListModal";
import {
  fetchHolidays,
  deleteHoliday,
} from "../../redux/holidaySlice";
import {
  fetchLeaveTypes,
  deleteLeaveType,
} from "../../redux/leaveSlice";
import {
  fetchLeaveLimits,
  deleteLeaveLimit,
  toggleLeaveLimitStatus,
} from "../../redux/leaveLimitSlice";
import { fetchFines, deleteFine } from "../../redux/fineSlice";
import {
  fetchSessions,
  addSession,
  updateSession,
  deleteSession,
  assignTeachersToSession,
} from "../../redux/sessionSlice";
import { fetchHrConfig, updateHrConfig } from "../../redux/hrConfigSlice";

const HrController = () => {
  const dispatch = useDispatch();
  const { holidays, status: holidayStatus, error: holidayError } = useSelector(
    (state) => state.holidays
  );
  const { leaveTypes, status: leaveStatus, error: leaveError } = useSelector(
    (state) => state.leaveTypes
  );
  const { leaveLimits, status: leaveLimitStatus, error: leaveLimitError } = useSelector(
    (state) => state.leaveLimits
  );
  const { fines, status: fineStatus, error: fineError } = useSelector((state) => state.fines);
  const { sessions, status: sessionStatus, error: sessionError } = useSelector(
    (state) => state.sessions
  );
  const { hrConfig, status: configStatus, error: configError } = useSelector(
    (state) => state.hrConfig
  );

  const [isHolidayModalOpen, setHolidayModalOpen] = useState(false);
  const [isLeaveModalOpen, setLeaveModalOpen] = useState(false);
  const [isLeaveLimitModalOpen, setLeaveLimitModalOpen] = useState(false);
  const [isFineModalOpen, setFineModalOpen] = useState(false);
  const [isSessionModalOpen, setSessionModalOpen] = useState(false);
  const [isTeacherListModalOpen, setTeacherListModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const defaultValues = useMemo(() => ({
    gratuityStart: hrConfig?.gratuityStart?.toString() || "365",
    maxLoanAllowed: hrConfig?.maxLoanAllowed?.toString() || "5",
    minutesPerDeduction: hrConfig?.minutesPerDeduction?.toString() || "15",
    maxInstallment: hrConfig?.maxInstallment?.toString() || "12",
    annualLeaves: hrConfig?.annualLeaves?.toString() || "21",
    sickLeaves: hrConfig?.sickLeaves?.toString() || "10",
    casualLeaves: hrConfig?.casualLeaves?.toString() || "7",
  }), [hrConfig]);

  const { register, handleSubmit, reset } = useForm({ defaultValues });

  useEffect(() => {
    dispatch(fetchHolidays());
    dispatch(fetchLeaveTypes());
    dispatch(fetchLeaveLimits());
    dispatch(fetchFines());
    dispatch(fetchSessions());
    dispatch(fetchHrConfig());
  }, [dispatch]);

  useEffect(() => {
    reset(defaultValues);
  }, [hrConfig, reset, defaultValues]);

  const onSubmit = (data) => {
    dispatch(updateHrConfig(data));
    alert("HR Configuration updated successfully!");
  };

  const handleSessionSubmit = async (sessionData) => {
    if (editItem) {
      // Update existing session - send only the sessionData part, not the nested structure
      const updateData = {
        sessionName: sessionData.sessionData.sessionName,
        start: sessionData.sessionData.start,
        end: sessionData.sessionData.end,
        status: sessionData.sessionData.status
      };
      
      try {
        // Update session data
        const sessionResult = await dispatch(updateSession({ id: editItem.id, updatedData: updateData }));
        
        // Update teacher assignments
        const teacherResult = await dispatch(assignTeachersToSession({ 
          sessionId: editItem.id, 
          teacherEmails: sessionData.teacherEmails 
        }));
        
        // Check if both operations were successful
        if (sessionResult.type.endsWith('/fulfilled') && teacherResult.type.endsWith('/fulfilled')) {
          // Refresh sessions list to show updated data
          dispatch(fetchSessions());
          alert('Session updated successfully!');
        } else {
          alert('Error updating session. Please try again.');
        }
      } catch (error) {
        console.error('Error updating session:', error);
        alert('Error updating session. Please try again.');
      }
    } else {
      // Create new session - send the full nested structure
      dispatch(addSession(sessionData));
    }
  };

  const handleViewTeachers = (session) => {
    setSelectedSession(session);
    setTeacherListModalOpen(true);
  };

  const handleEdit = (item, type) => {
    setEditItem(item);
    if (type === "holiday") setHolidayModalOpen(true);
    if (type === "leave") setLeaveModalOpen(true);
    if (type === "leaveLimit") setLeaveLimitModalOpen(true);
    if (type === "fine") setFineModalOpen(true);
    if (type === "session") {
      // SessionModal will handle fetching session teachers
      setSessionModalOpen(true);
    }
  };

  const handleDelete = async (id, type) => {
    if (type === "holiday") {
      await dispatch(deleteHoliday(id));
      dispatch(fetchHolidays()); // Refresh holidays list after deletion
    }
    if (type === "leave") dispatch(deleteLeaveType(id));
    if (type === "leaveLimit") dispatch(deleteLeaveLimit(id));
    if (type === "fine") dispatch(deleteFine(id));
    if (type === "session") dispatch(deleteSession(id));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Sessions Detail - Full Width */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold text-xl">Sessions Detail</h2>
          <button
            onClick={() => {
              setEditItem(null);
              setSessionModalOpen(true);
            }}
            className="bg-[#241763] text-white px-4 py-2 rounded hover:bg-[#1a0f4a] transition-colors"
          >
            📅 Add New Session
          </button>
        </div>
        {sessionStatus === "loading" && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#241763]"></div>
            <p className="mt-2 text-gray-600">Loading sessions...</p>
          </div>
        )}
        {sessionError && <p className="text-red-500 p-4 bg-red-50 rounded">{sessionError}</p>}
        
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-[#241763]/10 sticky top-0 z-10">
              <tr>
                <th className="border-b p-3 font-semibold">Session Name</th>
                <th className="border-b p-3 font-semibold">Start Date</th>
                <th className="border-b p-3 font-semibold">End Date</th>
                <th className="border-b p-3 font-semibold">Status</th>
                <th className="border-b p-3 font-semibold">Teachers</th>
                <th className="border-b p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="border-b p-3 font-medium">{session.sessionName}</td>
                  <td className="border-b p-3">{session.start ? session.start.split("T")[0] : "N/A"}</td>
                  <td className="border-b p-3">{session.end ? session.end.split("T")[0] : "N/A"}</td>
                  <td className="border-b p-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        session.status === "Running" 
                          ? "bg-green-100 text-green-800" 
                          : session.status === "Completed"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-orange-100 text-orange-800"
                      }`}
                    >
                      {session.status}
                    </span>
                  </td>
                  <td className="border-b p-3">
                    <button
                      onClick={() => handleViewTeachers(session)}
                      className="text-[#241763] hover:text-[#1a0f4a] font-medium text-sm underline"
                    >
                      View Teachers {session.teacherCount}
                    </button>
                  </td>
                  <td className="border-b p-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(session, "session")}
                        className="text-blue-500 hover:text-blue-700 p-1"
                        title="Edit Session"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(session.id, "session")}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete Session"
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {sessions.length === 0 && sessionStatus !== "loading" && (
          <div className="text-center py-8 text-gray-500">
            <p>No sessions found. Create your first session!</p>
          </div>
        )}
      </div>

      {/* Other Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Holidays Detail */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold">Holidays Detail</h2>
            <button
              onClick={() => {
                setEditItem(null);
                setHolidayModalOpen(true);
              }}
              className="bg-[#241763] text-white px-4 py-2 rounded hover:bg-[#1a0f4a] transition-colors"
            >
              🎁 Add New Holiday
            </button>
          </div>
          {holidayStatus === "loading" && <p>Loading...</p>}
          {holidayError && <p className="text-red-500">{holidayError}</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead className="bg-[#241763]/10 mb-2">
                <tr>
                  <th className="border-b p-2">Holiday Name</th>
                  <th className="border-b p-2">Start</th>
                  <th className="border-b p-2">End</th>
                  <th className="border-b p-2">Description</th>
                  <th className="border-b p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {holidays.map((holiday) => (
                  <tr key={holiday.id}>
                    <td className="border-b p-3">{holiday.name}</td>
                    <td className="border-b p-2">{holiday.start ? holiday.start.split("T")[0] : "N/A"}</td>
                    <td className="border-b p-2">{holiday.end ? holiday.end.split("T")[0] : "N/A"}</td>
                    <td className="border-b p-2">{holiday.description}</td>
                    <td className="border-b p-2">
                      <button
                        onClick={() => handleEdit(holiday, "holiday")}
                        className="text-blue-500 mr-2"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(holiday.id, "holiday")}
                        className="text-red-500"
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      {/* Leave Types */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">Leave Types</h2>
          <button
            onClick={() => {
              setEditItem(null);
              setLeaveModalOpen(true);
            }}
            className="bg-[#241763] text-white px-4 py-2 rounded"
          >
            <span className="text-lg">🤒</span> Add Leave Type
          </button>
        </div>
        {leaveStatus === "loading" && <p>Loading...</p>}
        {leaveError && <p className="text-red-500">{leaveError}</p>}
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-[#241763]/10">
            <tr>
              <th className="border-b p-2">Leave Type</th>
              <th className="border-b p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {leaveTypes.map((leave) => (
              <tr key={leave.id}>
                <td className="border-b p-3">{leave.leaveType}</td>
                <td className="border-b p-2">
                  <button
                    onClick={() => handleEdit(leave, "leave")}
                    className="text-blue-500 mr-2"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(leave.id, "leave")}
                    className="text-red-500"
                  >
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Leave Limits */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">Leave Limits</h2>
          <button
            onClick={() => {
              setEditItem(null);
              setLeaveLimitModalOpen(true);
            }}
            className="bg-[#241763] text-white px-4 py-2 rounded"
          >
            <span className="text-lg">📊</span> Add Leave Limit
          </button>
        </div>
        {leaveLimitStatus === "loading" && <p>Loading...</p>}
        {leaveLimitError && <p className="text-red-500">{leaveLimitError}</p>}
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-[#241763]/10">
            <tr>
              <th className="border-b p-2">Leave Type</th>
              <th className="border-b p-2">Max Days</th>
              <th className="border-b p-2">Paid</th>
              <th className="border-b p-2">Status</th>
              <th className="border-b p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {leaveLimits.map((limit) => (
              <tr key={limit.id}>
                <td className="border-b p-3">{limit.leaveType}</td>
                <td className="border-b p-3">{limit.maxDays}</td>
                <td className="border-b p-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    limit.isPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {limit.isPaid ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="border-b p-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    limit.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {limit.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="border-b p-2">
                  <button
                    onClick={() => handleEdit(limit, "leaveLimit")}
                    className="text-blue-500 mr-2"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => dispatch(toggleLeaveLimitStatus(limit.id))}
                    className={`mr-2 ${limit.isActive ? 'text-yellow-500' : 'text-green-500'}`}
                  >
                    {limit.isActive ? '⏸️' : '▶️'}
                  </button>
                  <button
                    onClick={() => handleDelete(limit.id, "leaveLimit")}
                    className="text-red-500"
                  >
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* HR Configuration */}
      <div className="bg-white rounded-lg shadow p-4 h-auto md:col-span-2">
        <form onSubmit={handleSubmit(onSubmit)} className="rounded-lg p-4 h-auto md:col-span-2">
          <h2 className="font-semibold mb-4">HR Configuration</h2>
          {configStatus === "loading" && <p>Loading...</p>}
          {configError && <p className="text-red-500">{configError}</p>}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Gratuity Start (Days)</label>
              <input
                type="text"
                {...register("gratuityStart", { required: "Required" })}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Max Loan Allowed (Times of Salary)
              </label>
              <input
                type="text"
                {...register("maxLoanAllowed", { required: "Required" })}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Minutes Per Deduction</label>
              <input
                type="text"
                {...register("minutesPerDeduction", { required: "Required" })}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Installment</label>
              <input
                type="text"
                {...register("maxInstallment", { required: "Required" })}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Annual Leaves (After Probation)
              </label>
              <input
                type="text"
                {...register("annualLeaves", { required: "Required" })}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Sick Leaves (Per Fiscal Year)
              </label>
              <input
                type="text"
                {...register("sickLeaves", { required: "Required" })}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Casual Leaves (Per Fiscal Year)
              </label>
              <input
                type="text"
                {...register("casualLeaves", { required: "Required" })}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="md:mt-6">
              <button
                type="submit"
                className="bg-[#241763] w-full text-white px-6 py-2 rounded"
              >
                Update
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Fine Detail */}
      <div className="bg-white rounded-lg shadow p-4 md:col-span-2">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">Fine Detail</h2>
          <button
            onClick={() => {
              setEditItem(null);
              setFineModalOpen(true);
            }}
            className="bg-[#241763] text-white px-4 py-2 rounded"
          >
            <span className="text-lg">💰</span> Add Fine
          </button>
        </div>
        {fineStatus === "loading" && <p>Loading...</p>}
        {fineError && <p className="text-red-500">{fineError}</p>}
        <div className="overflow-x-auto">
          <table className="table-auto w-full text-sm text-left border-collapse">
            <thead className="bg-[#241763]/10">
              <tr>
                <th className="border-b p-2">Emp Code</th>
                <th className="border-b p-2">Emp Name</th>
                <th className="border-b p-2">Fine Reason</th>
                <th className="border-b p-2">Fine Amount</th>
                <th className="border-b p-2">Date</th>
                <th className="border-b p-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {fines.map((fine) => (
                <tr key={fine.id}>
                  <td className="border-b p-3">{fine.employee?.cnic}</td>
                  <td className="border-b p-2">
                    {fine.employee?.firstName} {fine.employee?.lastName}
                  </td>
                  <td className="border-b p-2">{fine.fineReason}</td>
                  <td className="border-b p-2">${fine.fineAmount}</td>
                  <td className="border-b p-2">{fine.fineDate ? fine.fineDate.split("T")[0] : "N/A"}</td>
                  <td className="border-b p-2">
                    <button
                      onClick={() => handleEdit(fine, "fine")}
                      className="text-blue-500 mr-2"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(fine.id, "fine")}
                      className="text-red-500"
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {/* Modals */}
      <HolidayModal
        isOpen={isHolidayModalOpen}
        onClose={() => {
          setHolidayModalOpen(false);
          setEditItem(null);
        }}
        onSubmit={() => {}}
        holiday={editItem}
      />
      <LeaveModal
        isOpen={isLeaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        onSubmit={() => {}}
        leaveType={editItem}
      />
      <LeaveLimitModal
        isOpen={isLeaveLimitModalOpen}
        onClose={() => setLeaveLimitModalOpen(false)}
        editItem={editItem}
      />
      <FineModal
        isOpen={isFineModalOpen}
        onClose={() => setFineModalOpen(false)}
        onSubmit={() => {}}
        fine={editItem}
      />
      <SessionModal
        isOpen={isSessionModalOpen}
        onClose={() => setSessionModalOpen(false)}
        onSubmit={handleSessionSubmit}
        session={editItem}
      />
      <TeacherListModal
        isOpen={isTeacherListModalOpen}
        onClose={() => setTeacherListModalOpen(false)}
        sessionId={selectedSession?.id}
        sessionName={selectedSession?.sessionName}
      />
    </div>
  );
};

export default HrController;